import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Brain, 
  Music, 
  Star, 
  Users, 
  Github, 
  Database, 
  Shield, 
  Target,
  BarChart3,
  Globe,
  FileText,
  ExternalLink
} from 'lucide-react';

export function ResearchPage() {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl font-bold mb-4">AI Music Perception Research</h1>
        <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
          Open source annotation of AI/real music data to advance artificial intelligence research
        </p>
      </div>

      {/* Research Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-2xl">
            <Brain className="h-6 w-6 text-primary" />
            Research Overview
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="text-lg text-muted-foreground">
            <p className="mb-4">
              This research project aims to understand how people perceive and distinguish between AI-generated and human-composed music. 
              Your participation contributes to cutting-edge research in artificial intelligence and music cognition.
            </p>
            <p>
              The data collected will be released anonymously on Hugging Face and GitHub to help the AI research community 
              develop better music generation models and understand human perception of AI-generated content.
            </p>
          </div>

          {/* Key Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-blue-50 dark:bg-blue-950/50 rounded-lg border border-blue-200 dark:border-blue-800">
              <Database className="h-8 w-8 text-blue-600 mx-auto mb-3" />
              <h3 className="font-semibold mb-2">Open Source Data</h3>
              <p className="text-sm text-muted-foreground">
                All annotations will be released publicly for research use
              </p>
            </div>
            <div className="text-center p-4 bg-green-50 dark:bg-green-950/50 rounded-lg border border-green-200 dark:border-green-800">
              <Shield className="h-8 w-8 text-green-600 mx-auto mb-3" />
              <h3 className="font-semibold mb-2">Anonymous</h3>
              <p className="text-sm text-muted-foreground">
                Your personal information is never shared, only your annotations
              </p>
            </div>
            <div className="text-center p-4 bg-purple-50 dark:bg-purple-950/50 rounded-lg border border-purple-200 dark:border-purple-800">
              <Globe className="h-8 w-8 text-purple-600 mx-auto mb-3" />
              <h3 className="font-semibold mb-2">Global Impact</h3>
              <p className="text-sm text-muted-foreground">
                Help advance AI research worldwide through collaborative annotation
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Research Goals */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-2xl">
            <Target className="h-6 w-6 text-primary" />
            Research Goals
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Music className="h-5 w-5 text-primary" />
                  Music Perception
                </h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Understand how humans distinguish AI vs human music</li>
                  <li>• Identify key characteristics that signal authenticity</li>
                  <li>• Study emotional and aesthetic responses to different music types</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-primary" />
                  AI Development
                </h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Improve AI music generation models</li>
                  <li>• Develop better evaluation metrics for AI music</li>
                  <li>• Create datasets for training and testing AI systems</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Collection Process */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-2xl">
            <FileText className="h-6 w-6 text-primary" />
            Data Collection Process
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="bg-muted/30 p-4 rounded-lg">
              <h4 className="font-semibold mb-3">What We Collect:</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h5 className="font-medium mb-2">Annotations Include:</h5>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    <li>• Authenticity assessment (AI vs human)</li>
                    <li>• Quality ratings (aesthetic, emotional, technical)</li>
                    <li>• Mood and emotional tags</li>
                    <li>• Familiarity with the music</li>
                    <li>• Optional comments and descriptions</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-medium mb-2">What We Don't Collect:</h5>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    <li>• Personal identifying information</li>
                    <li>• Contact details or email addresses</li>
                    <li>• Location data</li>
                    <li>• Any data that could identify you</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-green-50 dark:bg-green-950/50 p-4 rounded-lg border border-green-200 dark:border-green-800">
              <h4 className="font-semibold mb-2 text-green-800 dark:text-green-200">Data Release</h4>
              <p className="text-sm text-green-700 dark:text-green-300">
                All collected annotations will be released as open source datasets on Hugging Face and GitHub. 
                This includes the music samples (with proper licensing) and all anonymous annotations, 
                enabling researchers worldwide to use this data for AI music research.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Technical Details */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-2xl">
            <Database className="h-6 w-6 text-primary" />
            Technical Implementation
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold mb-3">Music Sources</h3>
                <div className="space-y-2">
                  <Badge variant="outline" className="mr-2 mb-2">Suno AI</Badge>
                  <Badge variant="outline" className="mr-2 mb-2">Mureka</Badge>
                  <Badge variant="outline" className="mr-2 mb-2">Sonauto</Badge>
                  <Badge variant="outline" className="mr-2 mb-2">Udio</Badge>
                  <Badge variant="outline" className="mr-2 mb-2">Human-composed</Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  Mix of AI-generated and human-composed music from various sources
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-3">Annotation Framework</h3>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li>• GEMS emotional categories</li>
                  <li>• Multi-dimensional quality ratings</li>
                  <li>• Authenticity detection tasks</li>
                  <li>• Open-ended feedback collection</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Call to Action */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="p-6 text-center">
          <h3 className="text-xl font-semibold mb-3">Ready to Contribute?</h3>
          <p className="text-muted-foreground mb-4">
            Join our research community and help advance AI music understanding
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild size="lg" className="gap-2">
              <a href="/">
                <Users className="h-4 w-4" />
                Start Annotating
              </a>
            </Button>
            <Button asChild variant="outline" size="lg" className="gap-2">
              <a href="https://github.com/LAION-AI" target="_blank" rel="noopener noreferrer">
                <Github className="h-4 w-4" />
                View on GitHub
                <ExternalLink className="h-3 w-3" />
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
